import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  agreementMailPages,
  agreementTexts,
  buttonName,
  commonErrMsg,
  commonValidationMsg,
  identityText,
  optimizationTexts,
  signAgreementContent,
  tcAgreementTexts,
  tooltips
} from "../../../locales/en";
import HeaderDashboard from "../../../components/layout/header/HeaderDashboard";
import { FaSpinner } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import "../tcGuaranteeStep.scss";
import publicAxios from "../../../utils/publicAxios";
import {
  getCheckTokenValidityUrl,
  getLeagalAgreementDownloadTokenUrl,
  getLeagalAgreementUploadTokenUrl
} from "../../../utils/constants/apiEndPoints";
import { downloadFileFromResponse, showToast } from "../../../utils/utils";
import {
  INFO_EMAIL,
  MAILTO_INFO_EMAIL,
  MISSING_ACCESS_TOKEN_COOKIE,
  showToastType
} from "../../../utils/constants/appConstants";
import { FILE_SIZE_LIMIT } from "../../../utils/constants/appConstants";

function TCGuarantee() {
  const [searchParams] = useSearchParams();
  const [getToken, setToken] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);

  const fileInput = useRef(null);
  const [isExpired, setIsExpired] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [successMsg, setSuccessMsg] = useState(false);

  const CheckTokenValidity = useCallback(async () => {
    try {
      const response = await publicAxios.get(getCheckTokenValidityUrl, {
        params: { token: getToken }
      });

      setIsExpired(response.data.data.isUsed);
    } catch (error) {
      if (error?.response?.data?.message === MISSING_ACCESS_TOKEN_COOKIE) {
        // no need to show any toast message
      } else {
        showToast(error.response.data.message, showToastType.error);
      }
    }
  }, [getToken]);

  const handleDownload = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoading(true);
      const response = await publicAxios.get(
        getLeagalAgreementDownloadTokenUrl,
        {
          responseType: "arraybuffer",
          params: {
            token: getToken
          }
        }
      );
      downloadFileFromResponse(response);
    } catch {
      showToast(commonErrMsg.item11, showToastType.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.size > FILE_SIZE_LIMIT) {
      showToast(commonValidationMsg?.fileLimitError, showToastType.error);
      event.target.value = "";
      setFileName("");
      setSelectedFile(null);
      return;
    }

    setFileName(file.name);
    setSelectedFile(file);
  };

  const handleUploadButtonClick = async () => {
    if (!selectedFile) {
      showToast(commonValidationMsg?.fileUpload, showToastType.error);
      return;
    }

    try {
      setLoadingUpload(true);
      const formDataToSend = new FormData();
      formDataToSend.append("file", selectedFile, selectedFile.name);

      const config = {
        method: "post",
        url: getLeagalAgreementUploadTokenUrl,
        headers: {
          "Content-Type": "multipart/form-data"
        },
        params: {
          token: getToken
        },
        data: formDataToSend
      };

      const response = await publicAxios(config);

      if (response.status === 200) {
        showToast(
          response.data.message ?? optimizationTexts?.fileUploaded,
          showToastType.success
        );
        setFileName("");
        setSelectedFile(null);
        if (fileInput.current) {
          fileInput.current.value = "";
        }
        setSuccessMsg(true);
      }
    } catch (error) {
      if (error.response.status === 400) {
        showToast(
          agreementMailPages?.guaranteeNoID.unsupportedFile,
          showToastType.error
        );
      } else {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const openFileDialog = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  useEffect(() => {
    const token = searchParams.get("token");
    setToken(token);
  }, [searchParams]);

  useEffect(() => {
    if (getToken) CheckTokenValidity();
  }, [getToken]);

  return (
    <div className="steplist-wrap guaranteNoId guarantee-wrap mt-5">
      <HeaderDashboard />

      {isExpired === false ? (
        <div className="container">
          <div className="row">
            <div className="col-md-8 offset-md-2 mb-5">
              <div className="step-wrap">
                <div className="step-title mainH1">
                  <h1>{`${agreementMailPages?.guaranteeNoID?.title}`}</h1>
                </div>
                {successMsg ? (
                  <div className="aplyid-section">
                    <p>
                      {`${agreementTexts?.uploadDocsSuccessText} `}
                      <Link to={MAILTO_INFO_EMAIL}>{INFO_EMAIL}</Link>
                    </p>
                  </div>
                ) : (
                  <div className="step-content mt-3 mb-5">
                    <div className="aplyid-section">
                      <p className="mb-3">{tcAgreementTexts?.instruction}</p>
                      <p className="mb-1">
                        {tcAgreementTexts?.instructionContent1}
                      </p>
                      <p className="mb-1">
                        {tcAgreementTexts?.instructionContent2}
                      </p>
                      <p className="mb-3">
                        {tcAgreementTexts?.instructionContent3}
                      </p>
                      <p>
                        {`${signAgreementContent?.email?.help} `}
                        <Link to={MAILTO_INFO_EMAIL}>{INFO_EMAIL}</Link>
                      </p>

                      <div className="agreement-group-warp fundtap_agreement_xl mt-5">
                        <div className="row mb-2">
                          <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                            <label htmlFor="Download">
                              {agreementTexts?.downloadAgreement}
                            </label>
                          </div>
                          <div className="col-md-6">
                            <button
                              className={`btn btn-primary fundtap_agreement spinDiv ${loading ? "spinDivBlack" : ""}`}
                              id="Download"
                              onClick={handleDownload}
                            >
                              {buttonName.download}
                              {loading && (
                                <span className="loadSpin">
                                  <FaSpinner />
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                            <label htmlFor="uploadAgreement">
                              {agreementTexts?.uploadAgreement}
                            </label>
                          </div>
                          <div className="col-md-6">
                            <div className="upload-file">
                              {fileName && (
                                <p className="uploaded-file-name">{fileName}</p>
                              )}
                              <button
                                type="button"
                                className={`btn btn-primary fundtap_agreement spinDiv`}
                                onClick={openFileDialog}
                              >
                                {tooltips.upload}
                              </button>
                              <input
                                name="isFile"
                                type="file"
                                ref={fileInput}
                                onChange={handleFileChange}
                                className="d-none"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 d-flex pt-2 pb-2 align-items-center" />
                          <div className="col-md-6">
                            <div className="upload-file">
                              <button
                                type="button"
                                onClick={handleUploadButtonClick}
                                className={`btn btn-primary fundtap_agreement spinDiv ${loadingUpload ? "spinDivBlack" : ""}`}
                              >
                                {buttonName.submit}
                                {loadingUpload && (
                                  <span className="loadSpin">
                                    <FaSpinner />
                                  </span>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="expired-token-message text-center pt-5">
              <h3>{identityText?.tokenExpiredText}</h3>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default TCGuarantee;
